/**
 * CampaignTargetingPanel — embeddable venue/outlet-group targeting
 * builder, extracted from the old standalone AdSubmitTargeting page so
 * it can live as a tab inside CampaignDetail instead of its own route.
 *
 * Pre-fills from the campaign's existing PROPOSED/APPROVED approvals so
 * re-opening this tab shows what's already targeted. Submitting fans out
 * CampaignApproval rows (one per venue × outlet group) and calls
 * `onSubmitted` so the shell can refresh the campaign and switch to the
 * Analytics tab instead of navigating away.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import apiService from '../../services/api';
import { Campaign, OutletGroup, TargetingPreview, VenuePartner } from '../../types';

// ---- venue card props ----

interface VenueCardProps {
  venue: VenuePartner;
  groups: OutletGroup[];
  selectedGroupIds: Set<string>;
  onToggle: (groupId: string) => void;
  onRemove: () => void;
}

const VenueCard: React.FC<VenueCardProps> = ({
  venue,
  groups,
  selectedGroupIds,
  onToggle,
  onRemove,
}) => {
  // Sort ANY first, then SYSTEM_AUTO, then VENUE_CURATED — matches the
  // wireframe's reading order so the platform default is always the
  // first row.
  const ordered = useMemo(() => {
    const rank = (k: string) => (k === 'ANY' ? 0 : k === 'SYSTEM_AUTO' ? 1 : 2);
    return [...groups].sort(
      (a, b) =>
        rank(a.kind) - rank(b.kind) ||
        a.display_name.localeCompare(b.display_name),
    );
  }, [groups]);

  return (
    <Paper variant="outlined" sx={{ mb: 1.5, overflow: 'hidden', borderRadius: 2 }}>
      <Box
        sx={{
          px: 2.5,
          py: 1.5,
          background: '#F1F5F9',
          borderBottom: '1px solid #E2E8F0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box>
          <Typography component="span" sx={{ fontWeight: 700, color: '#0F172A', mr: 1 }}>
            {venue.display_name}
          </Typography>
        </Box>
        <IconButton size="small" onClick={onRemove} aria-label="Remove venue">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box
        sx={{
          p: 1,
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          gap: 1,
        }}
      >
        {ordered.map((g) => {
          const selected = selectedGroupIds.has(g.id);
          const badgeColor =
            g.kind === 'SYSTEM_AUTO'
              ? { bg: '#E0F4FF', fg: '#1D4ED8', label: 'SYSTEM' }
              : g.kind === 'ANY'
              ? { bg: '#FFEDD5', fg: '#c2410c', label: 'ANY' }
              : { bg: '#FFEDD5', fg: '#c2410c', label: 'VENUE' };
          return (
            <Box
              key={g.id}
              role="checkbox"
              tabIndex={0}
              aria-checked={selected}
              onClick={() => onToggle(g.id)}
              onKeyDown={(e) => {
                if (e.key === ' ' || e.key === 'Enter') {
                  e.preventDefault();
                  onToggle(g.id);
                }
              }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                px: 1.5,
                py: 1.25,
                borderRadius: 1.25,
                cursor: 'pointer',
                border: selected ? '1px solid #2563EB' : '1px solid transparent',
                background: selected ? '#E0F4FF' : 'transparent',
                transition: 'background 120ms',
                '&:hover': { background: selected ? '#E0F4FF' : '#F1F5F9' },
              }}
            >
              <Box
                sx={{
                  width: 18,
                  height: 18,
                  borderRadius: '5px',
                  border: '1.5px solid',
                  borderColor: selected ? '#2563EB' : '#94A3B8',
                  background: selected ? '#2563EB' : 'white',
                  display: 'grid',
                  placeItems: 'center',
                  flexShrink: 0,
                  color: 'white',
                  fontSize: 12,
                  fontWeight: 800,
                }}
              >
                {selected ? '✓' : ''}
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography component="span" sx={{ fontSize: 13, fontWeight: 600, color: '#0F172A' }}>
                    {g.display_name}
                  </Typography>
                  <Chip
                    label={badgeColor.label}
                    size="small"
                    sx={{
                      height: 16,
                      fontSize: 9,
                      fontWeight: 700,
                      letterSpacing: '0.06em',
                      bgcolor: badgeColor.bg,
                      color: badgeColor.fg,
                      '& .MuiChip-label': { px: 0.75 },
                    }}
                  />
                </Box>
                <Typography sx={{ fontSize: 11, color: '#64748B', mt: 0.25 }}>
                  {g.member_count} outlets
                  {g.description ? ` · ${g.description}` : ''}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Paper>
  );
};

// ---- main panel ----

interface CampaignTargetingPanelProps {
  campaign: Campaign;
  /** Called after a successful submit so the shell can refresh the
   *  campaign and switch to the Analytics tab. */
  onSubmitted: () => void;
}

const CampaignTargetingPanel: React.FC<CampaignTargetingPanelProps> = ({
  campaign,
  onSubmitted,
}) => {
  const campaignId = campaign.id;

  const [allVenues, setAllVenues] = useState<VenuePartner[]>([]);
  const [groupsByVenue, setGroupsByVenue] = useState<Record<string, OutletGroup[]>>({});

  const [selectedVenueIds, setSelectedVenueIds] = useState<string[]>([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState<Set<string>>(new Set());
  const [addVenueId, setAddVenueId] = useState<string>('');
  const [notes, setNotes] = useState('');

  const [preview, setPreview] = useState<TargetingPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const previewTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ---- initial load ----

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        const [venuesPage, approvalsPage] = await Promise.all([
          apiService.listVenuePartners({ limit: 200 }),
          // Existing approvals — pre-fills the targeting picker with
          // venues + outlet groups already in the campaign's booking
          // calendar (PROPOSED or APPROVED).
          apiService.listCampaignApprovals({
            campaign_id: campaignId,
            limit: 1000,
          }),
        ]);
        if (cancelled) return;
        setAllVenues(venuesPage.data || []);

        const activeApprovals = (approvalsPage.data || []).filter(
          (a) => a.status === 'PROPOSED' || a.status === 'APPROVED',
        );
        if (activeApprovals.length > 0) {
          const venueIds = Array.from(new Set(activeApprovals.map((a) => a.venue_partner_id)));
          setSelectedVenueIds(venueIds);

          const groupIds = new Set<string>();
          activeApprovals.forEach((a) => {
            if (a.outlet_group_id) groupIds.add(a.outlet_group_id);
          });
          setSelectedGroupIds(groupIds);

          const uniqueVenues = Array.from(new Set(activeApprovals.map((a) => a.venue_partner_id)));
          const groupBuckets: { vId: string; gs: OutletGroup[] }[] = await Promise.all(
            uniqueVenues.map((vId) =>
              apiService
                .listOutletGroupsForVenue(vId)
                .then((gs) => ({ vId, gs }))
                .catch(() => ({ vId, gs: [] as OutletGroup[] })),
            ),
          );
          if (cancelled) return;
          setGroupsByVenue((prev) => {
            const next = { ...prev };
            groupBuckets.forEach(({ vId, gs }) => {
              next[vId] = gs;
            });
            return next;
          });
        }
      } catch (e: any) {
        setError(e?.message || 'Failed to load targeting data');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [campaignId]);

  // ---- per-venue group lazy fetch ----

  const ensureVenueLoaded = useCallback(
    async (venueId: string) => {
      if (groupsByVenue[venueId]) return;
      try {
        const groups = await apiService.listOutletGroupsForVenue(venueId);
        setGroupsByVenue((prev) => ({ ...prev, [venueId]: groups }));
      } catch (e: any) {
        setError(e?.message || 'Failed to load outlet groups');
      }
    },
    [groupsByVenue],
  );

  // ---- targeting preview ----

  useEffect(() => {
    if (previewTimer.current) clearTimeout(previewTimer.current);
    const ids = Array.from(selectedGroupIds);
    if (ids.length === 0) {
      setPreview(null);
      return;
    }
    previewTimer.current = setTimeout(async () => {
      setPreviewLoading(true);
      try {
        const p = await apiService.targetingPreview({ outlet_group_ids: ids });
        setPreview(p);
      } catch (e: any) {
        setError(e?.message || 'Preview failed');
      } finally {
        setPreviewLoading(false);
      }
    }, 300);
    return () => {
      if (previewTimer.current) clearTimeout(previewTimer.current);
    };
  }, [selectedGroupIds]);

  // ---- handlers ----

  const handleAddVenue = () => {
    if (!addVenueId) return;
    if (selectedVenueIds.includes(addVenueId)) {
      setAddVenueId('');
      return;
    }
    ensureVenueLoaded(addVenueId);
    setSelectedVenueIds((prev) => [...prev, addVenueId]);
    setAddVenueId('');
  };

  const handleRemoveVenue = (venueId: string) => {
    setSelectedVenueIds((prev) => prev.filter((v) => v !== venueId));
    const venueGroups = groupsByVenue[venueId] || [];
    const venueGroupIds = new Set(venueGroups.map((g) => g.id));
    setSelectedGroupIds((prev) => {
      const next = new Set(prev);
      venueGroupIds.forEach((id) => next.delete(id));
      return next;
    });
  };

  const handleToggleGroup = (groupId: string) => {
    setSelectedGroupIds((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  };

  const handleSubmit = async () => {
    if (selectedGroupIds.size === 0) {
      if (selectedVenueIds.length === 0) {
        setError('Pick at least one venue before submitting.');
        return;
      }
      const venuesLabel =
        selectedVenueIds.length === 1 ? '1 venue' : `${selectedVenueIds.length} venues`;
      const ok = window.confirm(
        `No outlet group selected — this campaign will play on EVERY ` +
          `outlet under ${venuesLabel}.\n\n` +
          `Continue with venue-wide targeting? If you meant to limit it to ` +
          `specific outlet groups (e.g. only the Lift TVs), cancel and pick ` +
          `the groups first.`,
      );
      if (!ok) return;
    }

    const venueIds = new Set<string>();
    if (selectedGroupIds.size > 0) {
      selectedGroupIds.forEach((gid) => {
        for (const [vId, groups] of Object.entries(groupsByVenue)) {
          if (groups.some((g) => g.id === gid)) {
            venueIds.add(vId);
            break;
          }
        }
      });
    } else {
      selectedVenueIds.forEach((vId) => venueIds.add(vId));
    }

    setSubmitting(true);
    setError(null);
    try {
      const res = await apiService.submitCampaign({
        campaign_id: campaignId,
        venue_partner_ids: Array.from(venueIds),
        outlet_group_ids: Array.from(selectedGroupIds),
        notes,
      });
      const created = res.created?.length || 0;
      const skipped = res.skipped?.length || 0;
      setSuccess(
        `Submitted: ${created} approval${created === 1 ? '' : 's'} created` +
          (skipped > 0 ? `, ${skipped} skipped (already in flight)` : ''),
      );
      onSubmitted();
    } catch (e: any) {
      setError(e?.message || 'Submit failed');
    } finally {
      setSubmitting(false);
    }
  };

  // ---- derived data ----

  const venueById = useMemo(
    () => Object.fromEntries(allVenues.map((v) => [v.id, v])),
    [allVenues],
  );

  const addableVenues = useMemo(
    () => allVenues.filter((v) => !selectedVenueIds.includes(v.id)),
    [allVenues, selectedVenueIds],
  );

  const groupsSelectedCount = selectedGroupIds.size;

  // ---- render ----

  if (loading) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 240 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '1fr 380px' },
        gap: 4,
      }}
    >
      {/* ---- main column ---- */}
      <Box>
        {(campaign.assets?.length || 0) === 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            This campaign has no assets. Add one from the Assets tab before
            submitting.
          </Alert>
        )}

        {/* Targeting card */}
        <Paper variant="outlined" sx={{ p: 3, mb: 2, borderRadius: 3 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="baseline"
            sx={{ mb: 2 }}
          >
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 700,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                color: '#64748B',
              }}
            >
              Targeting
            </Typography>
            <Typography sx={{ fontSize: 12, color: '#94A3B8' }}>
              {selectedVenueIds.length} venue
              {selectedVenueIds.length === 1 ? '' : 's'} · {groupsSelectedCount}{' '}
              group{groupsSelectedCount === 1 ? '' : 's'} selected
            </Typography>
          </Stack>

          {selectedVenueIds.map((vId) => {
            const venue = venueById[vId];
            if (!venue) return null;
            const groups = groupsByVenue[vId] || [];
            return (
              <VenueCard
                key={vId}
                venue={venue}
                groups={groups}
                selectedGroupIds={selectedGroupIds}
                onToggle={handleToggleGroup}
                onRemove={() => handleRemoveVenue(vId)}
              />
            );
          })}

          {addableVenues.length > 0 ? (
            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
              <FormControl size="small" sx={{ flex: 1 }}>
                <InputLabel>Add venue</InputLabel>
                <Select
                  value={addVenueId}
                  label="Add venue"
                  onChange={(e) => setAddVenueId(e.target.value as string)}
                >
                  {addableVenues.map((v) => (
                    <MenuItem key={v.id} value={v.id}>
                      {v.display_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                disabled={!addVenueId}
                onClick={handleAddVenue}
              >
                Add
              </Button>
            </Stack>
          ) : (
            <Typography sx={{ fontSize: 12, color: '#94A3B8', mt: 1 }}>
              All venues added.
            </Typography>
          )}
        </Paper>

        <Paper variant="outlined" sx={{ p: 3, mb: 2, borderRadius: 3 }}>
          <Typography
            sx={{
              fontSize: 13,
              fontWeight: 700,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: '#64748B',
              mb: 2,
            }}
          >
            Notes for venue (optional)
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={2}
            placeholder="Anything the approving venue should know — campaign context, contact info, etc."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </Paper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        {campaign.state === 'DRAFT' ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <strong>Submitting publishes the campaign.</strong>{' '}
            Approval requests fan out to the venues you pick below
            and the campaign goes live. You can come back later to
            add more venues; existing approvals stay untouched.
          </Alert>
        ) : (
          <Alert severity="info" sx={{ mb: 2 }}>
            This campaign is <strong>{campaign.state}</strong>. Pick
            any venues you haven't targeted yet — they'll receive a
            fresh approval request. Venues already on this campaign
            are skipped automatically.
          </Alert>
        )}

        <Stack direction="row" spacing={1.5}>
          <Button
            variant="contained"
            disabled={submitting || groupsSelectedCount === 0}
            onClick={handleSubmit}
          >
            {submitting
              ? 'Submitting…'
              : campaign.state === 'DRAFT'
                ? 'Submit & publish'
                : 'Submit to venues'}
          </Button>
        </Stack>
      </Box>

      {/* ---- right rail ---- */}
      <Box sx={{ position: { md: 'sticky' }, top: 24, height: 'max-content' }}>
        <Paper
          variant="outlined"
          sx={{ p: 3, borderRadius: 3, boxShadow: '0 1px 2px rgba(120,90,60,0.06)' }}
        >
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              color: '#2563EB',
              mb: 1.5,
            }}
          >
            Coverage
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
            <Typography
              sx={{
                fontSize: 48,
                fontWeight: 800,
                color: '#0F172A',
                lineHeight: 1,
                letterSpacing: '-0.02em',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {preview ? preview.total_outlets : 0}
            </Typography>
            <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#64748B', pb: 0.75 }}>
              outlets
            </Typography>
            {previewLoading && <CircularProgress size={14} sx={{ mb: 1, ml: 1 }} />}
          </Box>
          <Typography sx={{ fontSize: 13, color: '#64748B', mt: 0.5 }}>
            {preview
              ? `across ${preview.total_cities} cit${
                  preview.total_cities === 1 ? 'y' : 'ies'
                } · ${preview.total_venues} venue${preview.total_venues === 1 ? '' : 's'}`
              : 'Pick a group to see coverage'}
          </Typography>

          {preview && preview.by_venue.length > 0 && (
            <Box sx={{ mt: 2.5, pt: 2, borderTop: '1px solid #E2E8F0' }}>
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: '#94A3B8',
                  mb: 1,
                }}
              >
                Outlets by venue
              </Typography>
              {preview.by_venue.map((row) => (
                <Box
                  key={row.venue_partner_id}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    py: 1,
                    borderBottom: '1px solid #E2E8F0',
                    '&:last-child': { borderBottom: 'none' },
                    fontSize: 13,
                  }}
                >
                  <Typography sx={{ color: '#64748B', fontSize: 13 }}>
                    {venueById[row.venue_partner_id]?.display_name || row.venue_partner_id}
                  </Typography>
                  <Typography sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', fontSize: 13 }}>
                    {row.outlets}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}

          {preview && preview.top_cities.length > 0 && (
            <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #E2E8F0' }}>
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: '#94A3B8',
                  mb: 1,
                }}
              >
                Top cities
              </Typography>
              {preview.top_cities.map((row) => (
                <Box
                  key={row.city}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    py: 1,
                    borderBottom: '1px solid #E2E8F0',
                    '&:last-child': { borderBottom: 'none' },
                    fontSize: 13,
                  }}
                >
                  <Typography sx={{ color: '#64748B', fontSize: 13 }}>{row.city}</Typography>
                  <Typography sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', fontSize: 13 }}>
                    {row.outlets}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}

          {preview && preview.exception_count > 0 && (
            <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mt: 2 }}>
              <strong>Heads-up:</strong> {preview.exception_count} outlet
              {preview.exception_count === 1 ? '' : 's'} in your selection
              block one of this ad's categories. They'll fan out as
              per-outlet decisions instead of bulk group approval.
            </Alert>
          )}
        </Paper>
      </Box>
    </Box>
  );
};

export default CampaignTargetingPanel;
